import { Form, Link, useNavigation, useSearchParams } from "react-router";
import { Search } from "lucide-react";
import { cn } from "@veo/ui";
import type { InsightsData } from "../lib/insights";
import { ArticleCard } from "./ArticleCard";
import { Breadcrumbs } from "./Breadcrumbs";
import { ArticleGridSkeleton, EmptyState } from "./Skeletons";
import { Reveal, RevealItem, RevealList } from "./motion";

export function InsightsListing({
  insights,
  category,
  search,
}: {
  insights: InsightsData;
  category?: string;
  search: string;
}) {
  const [searchParams] = useSearchParams();
  const navigation = useNavigation();
  const isLoading = navigation.state === "loading";

  const activeCategory = insights.categories.find((entry) => entry.slug === category);
  const { page, totalPages, total } = insights.meta;

  const pageHref = (target: number) => {
    const next = new URLSearchParams(searchParams);
    if (target <= 1) next.delete("page");
    else next.set("page", String(target));
    const query = next.toString();
    return query ? `?${query}` : "?";
  };

  return (
    <main id="main">
      <section className="bg-navy-900 textured-dark text-cream-100">
        <div className="container-site section-y">
          <Breadcrumbs
            onDark
            className="mb-6"
            items={
              activeCategory
                ? [{ label: "Insights", href: "/insights" }, { label: activeCategory.name }]
                : [{ label: "Insights" }]
            }
          />

          <div className="max-w-3xl">
            <p className="eyebrow text-gold-300">Insights</p>
            <h1 className="mt-4 text-5xl text-cream-50">
              {activeCategory ? activeCategory.name : "Market thinking, written plainly"}
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-navy-100">
              Research notes and practical guidance on offshore and local markets, retirement and
              the habits that compound over a lifetime.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-surface-0 textured">
        <div className="container-site section-y">
          <div className="flex flex-col gap-6 border-b border-ink-200 pb-6 lg:flex-row lg:items-center lg:justify-between">
            <nav aria-label="Article categories">
              <ul className="flex flex-wrap gap-2">
                <li>
                  <CategoryChip to="/insights" active={!category}>
                    All
                  </CategoryChip>
                </li>
                {insights.categories.map((entry) => (
                  <li key={entry.slug}>
                    <CategoryChip
                      to={`/insights/category/${entry.slug}`}
                      active={entry.slug === category}
                    >
                      {entry.name}
                      <span className="ml-1.5 text-xs opacity-60">{entry.count}</span>
                    </CategoryChip>
                  </li>
                ))}
              </ul>
            </nav>

            {/*
              A GET form, so searching produces a shareable URL and works with
              JavaScript unavailable. React Router intercepts it for a
              client-side navigation when it can.
            */}
            <Form method="get" role="search" className="relative w-full lg:w-72">
              <label htmlFor="insights-search" className="sr-only">
                Search articles
              </label>
              <Search
                aria-hidden
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-400"
              />
              <input
                id="insights-search"
                type="search"
                name="q"
                defaultValue={search}
                placeholder="Search articles"
                className="w-full rounded-md border border-ink-300 py-2.5 pl-9 pr-3 text-sm placeholder:text-ink-400 focus:border-navy-700 focus:outline-none focus:ring-2 focus:ring-navy-700/20"
              />
            </Form>
          </div>

          <div className="mt-10">
            {isLoading ? (
              <ArticleGridSkeleton count={insights.meta.perPage} />
            ) : insights.data.length === 0 ? (
              <EmptyState
                title={search ? `No articles match “${search}”` : "No articles yet"}
                description={
                  search
                    ? "Try a broader term, or browse everything we have published."
                    : "New writing is published here regularly. Please check back soon."
                }
                action={
                  <Link
                    to="/insights"
                    className="inline-block rounded-md bg-navy-900 px-5 py-2.5 text-sm font-semibold text-cream-50"
                  >
                    View all insights
                  </Link>
                }
              />
            ) : (
              <>
                <p className="sr-only" role="status">
                  {total} {total === 1 ? "article" : "articles"} found
                </p>

                <RevealList className="grid gap-10 md:grid-cols-2 lg:grid-cols-3">
                  {insights.data.map((post, index) => (
                    <RevealItem key={post.id}>
                      <ArticleCard post={post} priority={index < 3} />
                    </RevealItem>
                  ))}
                </RevealList>
              </>
            )}
          </div>

          {totalPages > 1 && (
            <Reveal>
              <nav aria-label="Pagination" className="mt-14 flex items-center justify-between">
                {page > 1 ? (
                  <Link
                    to={pageHref(page - 1)}
                    rel="prev"
                    className="rounded-md px-4 py-2.5 text-sm font-semibold text-navy-800 ring-1 ring-inset ring-ink-300 hover:bg-cream-200"
                  >
                    <span aria-hidden>←</span> Previous
                  </Link>
                ) : (
                  <span />
                )}

                <p className="text-sm text-ink-500">
                  Page {page} of {totalPages}
                </p>

                {page < totalPages ? (
                  <Link
                    to={pageHref(page + 1)}
                    rel="next"
                    className="rounded-md px-4 py-2.5 text-sm font-semibold text-navy-800 ring-1 ring-inset ring-ink-300 hover:bg-cream-200"
                  >
                    Next <span aria-hidden>→</span>
                  </Link>
                ) : (
                  <span />
                )}
              </nav>
            </Reveal>
          )}
        </div>
      </section>
    </main>
  );
}

function CategoryChip({
  to,
  active,
  children,
}: {
  to: string;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      // `aria-current` is what tells a screen reader which filter is applied;
      // the colour change alone conveys nothing without sight.
      {...(active ? { "aria-current": "page" as const } : {})}
      className={cn(
        "inline-flex items-center rounded-full px-4 py-2 text-sm transition-colors",
        active
          ? "bg-navy-900 font-semibold text-cream-50"
          : "text-ink-600 ring-1 ring-inset ring-ink-300 hover:bg-cream-200",
      )}
    >
      {children}
    </Link>
  );
}
